// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryDateGroupV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Date eyebrow + a stack of MidasHistoryRowV2 components.
// Date buckets per spec §1: TODAY / YESTERDAY / THIS WEEK / EARLIER.
// Eyebrow style mirrors the sidebar history bucket label (JBM 11 uppercase).
import React from "react";

import { Thread } from "../../../midasPageModels";
import MidasHistoryRowV2 from "./MidasHistoryRowV2";

export type DateBucket = "today" | "yesterday" | "thisWeek" | "earlier";

interface MidasHistoryDateGroupV2Props {
  bucket: DateBucket;
  threads: Thread[];
  /** Map of machineId → display name, built once in the page component. */
  machineNameById: Record<string, string>;
  /** Called when a row is clicked. */
  onOpenThread: (threadId: string) => void;
  /** Translation function — uses chatBot namespace keys. */
  t: (key: string, fallback: string) => string;
}

const bucketKey = (b: DateBucket): string => {
  switch (b) {
    case "today":
      return "midas.historyPage.bucketToday";
    case "yesterday":
      return "midas.historyPage.bucketYesterday";
    case "thisWeek":
      return "midas.historyPage.bucketThisWeek";
    case "earlier":
      return "midas.historyPage.bucketEarlier";
  }
};

const bucketFallback = (b: DateBucket): string => {
  switch (b) {
    case "today":
      return "TODAY";
    case "yesterday":
      return "YESTERDAY";
    case "thisWeek":
      return "THIS WEEK";
    case "earlier":
      return "EARLIER";
  }
};

const MidasHistoryDateGroupV2: React.FC<MidasHistoryDateGroupV2Props> = ({
  bucket,
  threads,
  machineNameById,
  onOpenThread,
  t,
}) => {
  if (threads.length === 0) return null;

  const label = t(bucketKey(bucket), bucketFallback(bucket));

  return (
    <section aria-label={label} className="flex flex-col gap-2">
      <h2
        className="font-mono text-[11px] uppercase tracking-[0.14em] text-midas-muted-stone"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {label}
      </h2>
      <ul className="flex flex-col gap-2">
        {threads.map((thread) => {
          const machineLabel = thread.machine_id
            ? machineNameById[thread.machine_id] || null
            : null;
          return (
            <MidasHistoryRowV2
              key={thread.id}
              thread={thread}
              machineLabel={machineLabel}
              onOpen={onOpenThread}
              t={t}
            />
          );
        })}
      </ul>
    </section>
  );
};

export default MidasHistoryDateGroupV2;
